import { Button } from "@/components/ui/button";
import { Check, Sparkles, ArrowUpRight, Crown, Zap, Shield, Star, TrendingUp, Plus, Clock, Gift } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import AnimatedSection from "@/components/AnimatedSection";
import instagramCounter from "@/assets/instagram-counter.jpg";

const Pricing = () => {
  const { t, language } = useLanguage();

  const scrollToContact = () => {
    const element = document.getElementById("contact");
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const plans = [
    {
      name: { fr: "Essentiel", en: "Essential" },
      description: {
        fr: "Idéal pour démarrer",
        en: "Perfect to get started",
      },
      price: { fr: "9,90", en: "9.90" },
      period: { fr: "/mois", en: "/month" },
      trialInfo: { fr: "1 mois d'essai gratuit", en: "1 month free trial" },
      commitmentInfo: { fr: "puis engagement 1 an", en: "then 1-year commitment" },
      includesPrevious: null,
      features: [
        { fr: "QR code personnalisé aux couleurs de votre marque", en: "Branded QR code with your colors" },
        { fr: "Redirection directe Instagram + Google", en: "Direct Instagram + Google redirect" },
        { fr: "Texte d'avis suggéré avec bouton Copier", en: "Suggested review text with Copy button" },
        { fr: "Dashboard analytics en temps réel", en: "Real-time analytics dashboard" },
        { fr: "Conforme RGPD, sans stockage de données clients", en: "GDPR compliant, no customer data stored" },
      ],
      popular: false,
      badge: null,
      icon: Zap,
      color: "from-rose-400 to-pink-500",
      accentColor: "rose",
      cardClass: "card-highlight-rose",
      buttonVariant: "outline" as const,
    },
    {
      name: { fr: "Pro", en: "Pro" },
      description: {
        fr: "Jusqu'à 5 points de vente",
        en: "Up to 5 locations",
      },
      price: { fr: "39,90", en: "39.90" },
      period: { fr: "/mois", en: "/month" },
      trialInfo: { fr: "1 mois d'essai gratuit", en: "1 month free trial" },
      commitmentInfo: { fr: "puis engagement 1 an", en: "then 1-year commitment" },
      includesPrevious: { fr: "Tout l'Essentiel +", en: "All Essential +" },
      features: [
        { fr: "Jusqu'à 5 points de vente", en: "Up to 5 locations" },
        { fr: "Intégration Trustpilot + TikTok", en: "Trustpilot + TikTok integration" },
        { fr: "En plus d'Instagram et Google", en: "In addition to Instagram and Google" },
        { fr: "Compteur Instagram OFFERT (valeur ~300€)", en: "FREE Instagram Counter (worth ~€300)", highlight: true },
      ],
      popular: true,
      badge: { fr: "Le plus populaire", en: "Most popular" },
      icon: TrendingUp,
      color: "from-violet-500 to-purple-600",
      accentColor: "violet",
      cardClass: "card-highlight-violet",
      buttonVariant: "default" as const,
    },
    {
      name: { fr: "Enterprise", en: "Enterprise" },
      description: {
        fr: "Points de vente illimités",
        en: "Unlimited locations",
      },
      price: { fr: "Sur devis", en: "Custom" },
      period: null,
      trialInfo: null,
      commitmentInfo: null,
      includesPrevious: { fr: "Tout le Pro +", en: "All Pro +" },
      features: [
        { fr: "Points de vente illimités", en: "Unlimited locations" },
        { fr: "Intégration toutes plateformes", en: "All platforms integration" },
        { fr: "Tous réseaux sociaux", en: "All social networks" },
        { fr: "Toutes plateformes d'avis", en: "All review platforms" },
        { fr: "Account manager dédié", en: "Dedicated account manager" },
      ],
      popular: false,
      badge: { fr: "Premium", en: "Premium" },
      icon: Shield,
      color: "from-amber-500 to-orange-500",
      accentColor: "amber",
      cardClass: "card-highlight-orange",
      buttonVariant: "outline" as const,
    },
  ];

  return (
    <section id="pricing" className="py-12 lg:py-16 bg-slate-50/50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <AnimatedSection className="max-w-3xl mx-auto text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-rose-100 to-pink-100 border border-rose-200/50 backdrop-blur-sm mb-6 shadow-sm">
            <Sparkles className="w-4 h-4 text-rose-500" />
            <span className="text-sm font-semibold bg-gradient-to-r from-rose-600 to-pink-600 bg-clip-text text-transparent">
              {language === "fr" ? "Tarification simple" : "Simple pricing"}
            </span>
          </div>
          
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold mb-4 text-slate-800">
            {language === "fr" ? "Boostez vos " : "Boost your "}
            <span className="bg-gradient-to-r from-rose-400 via-pink-500 to-rose-400 bg-clip-text text-transparent animate-gradient-shine">
              {language === "fr" ? "avis clients" : "customer reviews"}
            </span>
          </h2>
          <p className="text-lg text-slate-500 max-w-xl mx-auto">
            {language === "fr" 
              ? "Des formules adaptées à chaque commerce pour transformer vos clients en ambassadeurs"
              : "Plans designed for every business to turn customers into ambassadors"}
          </p>
        </AnimatedSection>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 lg:gap-6 max-w-6xl mx-auto items-stretch pt-6">
          {plans.map((plan, index) => (
            <AnimatedSection
              key={index}
              animation="scale"
              delay={index * 0.12}
              className={`overflow-visible ${plan.popular ? "md:scale-[1.02] md:z-10" : ""}`}
            >
              <div
                className={`group relative rounded-2xl ${plan.cardClass} !overflow-visible transition-all duration-500 hover:-translate-y-2 flex flex-col h-full ${
                  plan.popular
                    ? "ring-2 ring-purple-400/50 shadow-xl shadow-purple-500/15 p-7 lg:p-9"
                    : "p-6 lg:p-8"
                }`}
              >
                {/* Badge visible */}
                {plan.badge && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-40">
                    <span className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-bold shadow-xl ${
                      plan.popular 
                        ? "bg-purple-600 text-white shadow-purple-600/40" 
                        : "bg-amber-500 text-slate-900 shadow-amber-500/40"
                    }`} style={{ color: plan.popular ? '#ffffff' : '#1e293b' }}>
                      {plan.popular ? <Crown className="w-4 h-4" /> : <Star className="w-4 h-4" />}
                      {plan.badge[language]}
                    </span>
                  </div>
                )}
                
                {/* Glass shine effect - subtle */}
                <div className="absolute inset-0 rounded-2xl overflow-hidden pointer-events-none z-30">
                  <div className="absolute inset-0 w-1/2 bg-gradient-to-r from-transparent via-white/10 to-transparent opacity-0 group-hover:opacity-100 group-hover:animate-shine" />
                </div>
                
                {/* Glow effect on hover */}
                <div className={`absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none`}>
                  <div className={`absolute -inset-0.5 bg-gradient-to-r ${plan.color} rounded-2xl blur-md opacity-20`} />
                </div>

                <div className="relative z-10 flex flex-col h-full">
                  {/* Header with icon */}
                  <div className="text-center mb-6">
                    <div className={`inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br ${plan.color} mb-4 shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                      <plan.icon className="h-6 w-6 text-white" />
                    </div>
                    <h3 className="text-xl font-display font-bold text-slate-800">
                      {plan.name[language]}
                    </h3>
                    <p className="text-sm text-slate-500 mt-1">
                      {plan.description[language]}
                    </p>
                  </div>

                  {/* Price section - fixed height to align buttons */}
                  <div className="text-center mb-6 pb-6 border-b border-slate-200/60 min-h-[100px] flex flex-col justify-end">
                    {/* Main price */}
                    <div className="flex items-baseline justify-center gap-1">
                      <span className={`text-4xl lg:text-5xl font-display font-bold bg-gradient-to-r ${plan.color} bg-clip-text text-transparent transition-transform duration-300 inline-block group-hover:scale-105`}>
                        {plan.price[language]}{plan.period && "€"}
                      </span>
                      {plan.period && (
                        <span className="text-lg text-slate-500 font-medium">
                          {plan.period[language]}
                        </span>
                      )}
                    </div>
                    {/* Trial and commitment info */}
                    {plan.trialInfo && (
                      <div className="mt-3 space-y-1">
                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-emerald-100 text-emerald-700 border border-emerald-200">
                          <Sparkles className="w-3 h-3" />
                          {plan.trialInfo[language]}
                        </span>
                        {plan.commitmentInfo && (
                          <p className="text-xs text-slate-400">{plan.commitmentInfo[language]}</p>
                        )}
                      </div>
                    )}
                  </div>

                  {/* CTA Button */}
                  <Button
                    onClick={scrollToContact}
                    className={`w-full font-semibold py-5 rounded-full transition-all duration-300 hover:scale-[1.02] mb-5 ${
                      plan.popular 
                        ? "bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-400 hover:to-purple-500 text-white shadow-lg shadow-violet-500/30" 
                        : index === 0
                          ? "bg-gradient-to-r from-rose-400 to-pink-500 hover:from-rose-300 hover:to-pink-400 text-white shadow-lg shadow-rose-500/25"
                          : "bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-white shadow-lg shadow-amber-500/25"
                    }`}
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    {index === 2 
                      ? (language === "fr" ? "Nous contacter" : "Contact us")
                      : (language === "fr" ? `Essayer ${plan.name[language]}` : `Try ${plan.name[language]}`)}
                  </Button>

                  {/* Includes previous plan badge */}
                  {plan.includesPrevious && (
                    <div className="mb-4 text-center">
                      <span className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold text-slate-700 border border-slate-200`} style={{ background: `linear-gradient(to right, ${plan.accentColor === 'violet' ? 'rgb(243 232 255)' : plan.accentColor === 'amber' ? 'rgb(254 243 199)' : 'rgb(255 241 242)'}, ${plan.accentColor === 'violet' ? 'rgb(243 232 255)' : plan.accentColor === 'amber' ? 'rgb(254 243 199)' : 'rgb(255 241 242)'})` }}>
                        {plan.includesPrevious[language]}
                      </span>
                    </div>
                  )}

                  {/* Instagram Counter Image for Pro plan */}
                  {index === 1 && (
                    <div className="mb-4 p-2 rounded-xl bg-gradient-to-br from-violet-50 to-purple-50 border border-violet-100">
                      <div className="flex items-center gap-1.5 mb-1.5">
                        <Gift className="w-3 h-3 text-violet-500" />
                        <span className="text-[10px] font-bold text-violet-600 uppercase tracking-wide">
                          {language === "fr" ? "Bonus inclus" : "Included bonus"}
                        </span>
                      </div>
                      <img 
                        src={instagramCounter} 
                        alt={language === "fr" ? "Compteur Instagram physique offert" : "Free physical Instagram counter"}
                        className="w-3/4 mx-auto h-auto rounded-lg shadow-sm"
                      />
                      <p className="text-[10px] text-slate-500 mt-1.5 text-center">
                        {language === "fr" ? "Compteur Instagram live (valeur ~300€)" : "Live Instagram counter (worth ~€300)"}
                      </p>
                    </div>
                  )}

                  {/* Features */}
                  <ul className="space-y-3 flex-grow">
                    {plan.features.map((feature, idx) => (
                      <li 
                        key={idx} 
                        className="flex items-start gap-3 transition-all duration-300 group-hover:translate-x-1"
                        style={{ transitionDelay: `${idx * 0.03}s` }}
                      >
                        <div className={`flex-shrink-0 w-5 h-5 rounded-full bg-gradient-to-r ${plan.color} flex items-center justify-center mt-0.5 shadow-sm`}>
                          <Check className="h-3 w-3 text-white" />
                        </div>
                        <span className="text-sm text-slate-600 group-hover:text-slate-700 transition-colors">
                          {feature[language]}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </AnimatedSection>
          ))}
        </div>

        {/* Trust badges */}
        <AnimatedSection className="mt-12 text-center" delay={0.5}>
          <div className="inline-flex flex-wrap items-center justify-center gap-4 lg:gap-8 px-6 py-4 rounded-2xl bg-white/60 backdrop-blur-sm border border-slate-200/50 shadow-sm">
            <div className="flex items-center gap-2 text-slate-600">
              <Shield className="w-4 h-4 text-emerald-500" />
              <span className="text-sm font-medium">
                {language === "fr" ? "Garantie 30 jours" : "30-day guarantee"}
              </span>
            </div>
            <div className="hidden sm:block w-px h-4 bg-slate-300" />
            <div className="flex items-center gap-2 text-slate-600">
              <Clock className="w-4 h-4 text-amber-500" />
              <span className="text-sm font-medium">
                {language === "fr" ? "Mise en place rapide" : "Quick setup"}
              </span>
            </div>
            <div className="hidden sm:block w-px h-4 bg-slate-300" />
            <div className="flex items-center gap-2 text-slate-600">
              <Star className="w-4 h-4 text-rose-500" />
              <span className="text-sm font-medium">
                {language === "fr" ? "Support réactif" : "Responsive support"}
              </span>
            </div>
          </div>
          
          <p className="text-sm text-slate-400 mt-6">
            {language === "fr"
              ? "Besoin d'une solution sur-mesure ? "
              : "Need a custom solution? "}
            <button
              onClick={scrollToContact}
              className="text-rose-500 hover:text-rose-600 font-medium transition-colors duration-300 underline underline-offset-2"
            >
              {language === "fr" ? "Contactez-nous" : "Contact us"}
            </button>
          </p>
        </AnimatedSection>
      </div>
    </section>
  );
};

export default Pricing;